import React, {
  type CSSProperties,
  type FC,
  type PropsWithChildren,
  type ReactNode,
  useMemo,
  useState,
  useEffect
} from "react";
import "./style_modal_2.scss";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import KC_Icon_Button from "../KC_Button/KC_Icon_Button";
import { buildCls } from "../../utils/helpers/buildCls";

interface Props {
  id?: string;
  isOpen?: boolean;
  onClose: () => void;
  header?: ReactNode;
  footer?: ReactNode;
  hideCloseButton?: boolean;
  isFullPage?: boolean;
  extraStyle?: CSSProperties;
  className?: string;
  allowOverflow?: boolean;
  ref?: any;
}

const KC_Modal_2: FC<PropsWithChildren<Props>> = (props) => {
  const [shouldRender, setShouldRender] = useState(false);
  const [animationState, setAnimationState] = useState<
    "entering" | "entered" | "exiting" | "exited"
  >("exited");

  useEffect(() => {
    if (props.isOpen && animationState === "exited") {
      setShouldRender(true);
      requestAnimationFrame(() => {
        setAnimationState("entering");
      });
    } else if (!props.isOpen && animationState !== "exited") {
      setAnimationState("exiting");
    }
  }, [props.isOpen, animationState]);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && props.isOpen) {
        props.onClose();
      }
    };

    if (props.isOpen) {
      document.addEventListener("keydown", handleEscapeKey);
    }

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [props.isOpen, props.onClose]);

  // Handle animation end
  const handleAnimationEnd = () => {
    if (animationState === "entering") {
      setAnimationState("entered");
    } else if (animationState === "exiting") {
      setAnimationState("exited");
      setShouldRender(false);
    }
  };

  const classesName = useMemo(() => {
    const cls = [
      "modal-overlay",
      `modal-${animationState}`,
      props.className ?? "",
      props.allowOverflow ? "modal-display-overflow" : ""
    ];
    return buildCls(cls);
  }, [props.className, props.allowOverflow, animationState]);

  const contentClassName = useMemo(() => {
    return `modal-content modal-content-${animationState}`;
  }, [animationState]);

  if (!shouldRender) {
    return null;
  }

  return (
    <div
      className={classesName}
      onAnimationEnd={
        animationState === "exiting" ? handleAnimationEnd : undefined
      }
    >
      {!props.isFullPage && (
        <div
          id={props.id}
          className={contentClassName}
          style={props.extraStyle}
          onAnimationEnd={
            animationState === "entering" ? handleAnimationEnd : undefined
          }
        >
          <div className="kc-modal-header border-bottom">
            <span>{props.header}</span>
            {!props.hideCloseButton && (
              <KC_Icon_Button
                icon={faTimes}
                className="kc-modal-close"
                onClick={props.onClose}
                isInverted={true}
              />
            )}
          </div>
          <div className="kc-modal-body">{props.children}</div>
          {props.footer && <div className="modal-footer">{props.footer}</div>}
        </div>
      )}
      {props.isFullPage && (
        <div
          className={`modal-fullpage-content modal-content-${animationState}`}
          onAnimationEnd={
            animationState === "entering" ? handleAnimationEnd : undefined
          }
        >
          {props.children}
        </div>
      )}
    </div>
  );
};

export default KC_Modal_2;
